import {
  Button,
  ButtonGroup,
  Card,
  DateInput,
  ErrorSummary,
  ExternalLink,
  Heading,
  Hero,
  HouseIllustration,
  Page,
  TextInput,
  useBrand,
  Notification,
  useAccessTokenManagement
} from '@hbo/ui-servicing-react-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { Modal } from '@zambezi/sdk/modal';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthenticationService } from 'services/authentication';
import { RoutePath } from 'types/global';
import { onKeyDownNumericInputOnly } from 'utils/validation';
import './FourQuestionVerification.scss';
import { schema } from './config';

interface Schema {
  accountNumber?: string;
  dob?: string;
  surname?: string;
  postcode?: string;
}

const FourQuestionVerification: React.FC = () => {
  const navigate = useNavigate();
  const { brand } = useBrand();
  const { t } = useTranslation();
  const { isAuthenticated } = useAccessTokenManagement();

  // const { track } = useDataLayer();

  const {
    mutate,
    isPending: isLoading,
    isError,
    error
  } = useMutation({
    mutationFn: (data: AuthenticationService.Payload) => AuthenticationService.authenticate(data),
    onSuccess: (data, payload) => {
      if (data.customStatusCode === AuthenticationService.CustomStatusCode.AccountLocked) {
        return navigate(RoutePath.Locked, { replace: true, state: { reason: '4QV' } });
      }

      if (
        [AuthenticationService.CustomStatusCode.MobileNumberNotPresent].includes(
          data.customStatusCode
        )
      ) {
        // track('EXCEPTION: Error page - Reason - Mobile Number Not Present', null, 'Error');
        return navigate(RoutePath.Error, { replace: true });
      } else if (
        [AuthenticationService.CustomStatusCode.HygieneCheckFailed].includes(data.customStatusCode)
      ) {
        // track('EXCEPTION: Error page - Reason - Hygiene Check Failed', null, 'Error');
        return navigate(RoutePath.Error, { replace: true, state: { hygieneCheckFailed: true } });
      }

      navigate(RoutePath.ConfirmMobileNumber, {
        state: {
          ...data,
          ...payload
        }
      });
    }
  });

  const { control, handleSubmit } = useForm({
    mode: 'onBlur',
    //@ts-ignore
    resolver: yupResolver(schema)
  });

  const [isOneTimePasscodeModalActive, setIsOneTimePasscodeModalActive] = useState(false);

  const onSubmit: SubmitHandler<Schema> = (data) => {
    // @ts-expect-error
    mutate(data);
  };

  if (isAuthenticated) {
    return <Navigate to={RoutePath.CaseTracking} replace />;
  }

  return (
    <>
      <Helmet>
        <title>{t('4qv.tabTitle')}</title>
      </Helmet>
      <Hero size="lg">
        <Hero.PrimaryContent title={t('4qv.welcomeText')} />
        <Hero.SecondaryContent>
          <Hero.Illustration>
            <HouseIllustration />
          </Hero.Illustration>
        </Hero.SecondaryContent>
      </Hero>
      <Page>
        <Page.Content>
          <Heading level={3} as="h2" alternative>
            {t('4qv.heading1')}
          </Heading>
        </Page.Content>
        <Page.Content>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="hbo-notification-container">
              <Notification variant="info" title={t('4qv.notification.otp.title')}>
                <span className="sr-only">{t('generic.screenReaderInfo')}</span>
                <span className="otp-notification-text--tablet">
                  {t('4qv.notification.otp.content.desktop')}
                </span>
                <span className="otp-notification-text--mobile">
                  {t('4qv.notification.otp.content.mobile')}
                </span>
                <span className="otp-notification-text--mobile">
                  <Button
                    variant="link"
                    className="otp-notification-text--mobile"
                    onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                      const pageName = t('4qv.tabTitle');
                      // track(
                      //   `CLICK: ${pageName} - ${t('4qv.notification.otp.action')} - Button`,
                      //   getInternalLinkElement(event),
                      //   pageName
                      // );
                      setIsOneTimePasscodeModalActive(true);
                    }}
                  >
                    {t('4qv.notification.otp.action')}
                  </Button>
                </span>
              </Notification>
            </div>

            <Card>
              <Card.Header>{t('4qv.heading2')}</Card.Header>
              <Card.Body>
                {isError && (
                  <ErrorSummary>
                    <span className="sr-only">{t('generic.screenReaderError')}</span>
                    {error.message}
                  </ErrorSummary>
                )}
                <Controller
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    fieldState: { error }
                  }) => (
                    <TextInput
                      label={t('4qv.label.accountNumber')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      tooltip={t('4qv.label.accountNumber.tooltip')}
                      tooltipAriaLabel="More information about mortgage account number"
                      ref={ref}
                      error={error?.message}
                      inputProps={{
                        onKeyDown: onKeyDownNumericInputOnly,
                        maxLength: 8,
                        pattern: '\\d*'
                      }}
                      pii
                    />
                  )}
                  name="accountNumber"
                  control={control}
                />
                <Controller
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    fieldState: { error }
                  }) => (
                    <DateInput
                      label={t('4qv.label.dateOfBirth')}
                      name={name}
                      value={value}
                      onBlur={({ day, month, year, date }) => {
                        if ((day && month && year) || date || date === '') {
                          onChange(date);
                          onBlur();
                        }
                      }}
                      ref={ref}
                      error={error?.message}
                      pii
                    />
                  )}
                  name="dob"
                  control={control}
                />
                <Controller
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    fieldState: { error }
                  }) => (
                    <TextInput
                      label={t('4qv.label.surname')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ref={ref}
                      error={error?.message}
                      pii
                    />
                  )}
                  name="surname"
                  control={control}
                />
                <Controller
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    fieldState: { error }
                  }) => (
                    <TextInput
                      label={t('4qv.label.postCode')}
                      name={name}
                      value={value}
                      tooltip={t('4qv.label.postCode.tooltip')}
                      tooltipAriaLabel="More information about postcode"
                      onChange={onChange}
                      onBlur={onBlur}
                      ref={ref}
                      error={error?.message}
                      pii
                    />
                  )}
                  name="postcode"
                  control={control}
                />
              </Card.Body>
            </Card>
            <p>
              <Trans
                i18nKey="4qv.line0"
                components={[
                  // @ts-ignore turn off warnings about no children being passed in
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <ExternalLink
                    href={t(`4qv.chatLink.${brand}`)}
                    onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                      // const pageName = t('4qv.tabTitle');
                      // track(
                      //   `CLICK: ${pageName} - accessibility pages - Link`,
                      //   getExternalLinkElement(event),
                      //   pageName
                      // );
                    }}
                  />
                ]}
              />
            </p>
            <p>
              <Trans
                i18nKey="4qv.line1"
                components={[
                  // @ts-ignore turn off warnings about no children being passed in
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <ExternalLink
                    href={t(`4qv.accessibilityLinks.${brand}`)}
                    onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                      // const pageName = t('4qv.tabTitle');
                      // track(
                      //   `CLICK: ${pageName} - accessibility pages - Link`,
                      //   getExternalLinkElement(event),
                      //   pageName
                      // );
                    }}
                  />
                ]}
              />
            </p>
            <p>{t(`4qv.relay.${brand}`)}</p>
            <ButtonGroup>
              <Button
                type="submit"
                isLoading={isLoading}
                data-cy="login-button"
                onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                  const pageName = t('4qv.tabTitle');
                  // navigate(RoutePath.ConfirmMobileNumber)
                  // track(
                  //   `CLICK: ${pageName} - ${t('generic.login')} - Button`,
                  //   getInternalLinkElement(event),
                  //   pageName
                  // );
                }}
              >
                {t('generic.login')}
              </Button>
            </ButtonGroup>
          </form>
        </Page.Content>
      </Page>
      {isOneTimePasscodeModalActive && (
        <Modal
          confirm={t('4qv.modal.otp.action') as string}
          onConfirm={() => setIsOneTimePasscodeModalActive(false)}
          onCancel={() => setIsOneTimePasscodeModalActive(false)}
          title={t('4qv.modal.otp.title')}
          open={true}
          isClosable={true}
        >
          {t('4qv.modal.otp.content')}
        </Modal>
      )}
    </>
  );
};

export default FourQuestionVerification;
